import * as React from 'react';
import * as NavigationMenuPrimitive from '@radix-ui/react-navigation-menu';
import { cva } from 'class-variance-authority';

import { cn } from '@/lib/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';

function NavigationMenu({
  className,
  children,
  ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.Root> & {
  viewport?: boolean;
}) {
  return (
    <NavigationMenuPrimitive.Root
      data-slot="navigation-menu"
      className={cn(
        'relative z-10 flex max-w-max flex-1 items-center justify-center',
        className,
      )}
      {...props}>
      {children}
    </NavigationMenuPrimitive.Root>
  );
}

function NavigationMenuList({
  className,
  ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.List>) {
  return (
    <NavigationMenuPrimitive.List
      data-slot="navigation-menu-list"
      className={cn(
        'group/navList flex flex-1 list-none items-center justify-center space-x-1',
        className,
      )}
      {...props}
    />
  );
}

function NavigationMenuItem({
  className,
  ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.Item>) {
  return (
    <NavigationMenuPrimitive.Item
      data-slot="navigation-menu-item"
      className={cn('relative', className)}
      {...props}
    />
  );
}

const navigationMenuTriggerStyle = cva(
  'group/navTrig inline-flex h-9 w-max gap-2 items-center justify-center rounded-md bg-transparent px-2 py-1.5 text-sm font-semibold text-neutrals-800 transition-colors hover:bg-neutrals-200 focus:bg-transparent focus:outline-hidden disabled:pointer-events-none disabled:opacity-50  data-[state=open]:bg-neutrals-200 focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-primary-blue-600',
);

const activeBorderElementStyle = cva(
  `after:content-[''] after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[3px] after:rounded-tl-md after:rounded-tr-md after:bg-primary-blue-600`,
);

function NavigationMenuTrigger({
  className,
  children,
  active,
  ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.Trigger> & {
  active?: boolean;
}) {
  return (
    <div
      className={cn(
        'h-16 flex items-center relative',
        active && activeBorderElementStyle(),
      )}>
      <NavigationMenuPrimitive.Trigger
        onClick={(e) => e.preventDefault()}
        data-slot="navigation-menu-trigger"
        className={cn(
          className,
          navigationMenuTriggerStyle(),
          'group/navTrig relative',
        )}
        {...props}>
        {children}{' '}
        <FontAwesomeIcon
          icon={faChevronDown}
          className="relative top-px text-neutrals-700 h-3 w-3 transition duration-300 group-data-[state=open]/navTrig:rotate-180"
          aria-hidden="true"
        />
      </NavigationMenuPrimitive.Trigger>
    </div>
  );
}

function NavigationMenuContent({
  className,
  ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.Content>) {
  return (
    <NavigationMenuPrimitive.Content
      data-slot="navigation-menu-content"
      className={cn(
        'top-full z-10 w-auto data-[motion^=from-]:animate-in data-[motion^=to-]:animate-out data-[motion^=from-]:fade-in data-[motion^=to-]:fade-out data-[motion=from-end]:slide-in-from-right-52 data-[motion=from-start]:slide-in-from-left-52 data-[motion=to-end]:slide-out-to-right-52 data-[motion=to-start]:slide-out-to-left-52 md:absolute md:w-auto',
        'origin-top-center relative -mt-2 p-2 h-(--radix-navigation-menu-viewport-height) w-full overflow-hidden rounded-md border border-neutrals-200 bg-white text-neutrals-900 shadow-xs data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-90 md:w-(--radix-navigation-menu-viewport-width)',
        className,
      )}
      {...props}
    />
  );
}

function NavigationMenuLink({
  className,
  active,
  ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.Link>) {
  return (
    <div
      className={cn(
        'relative h-16 flex items-center',
        active && activeBorderElementStyle(),
      )}>
      <NavigationMenuPrimitive.Link
        data-slot="navigation-menu-link"
        className={cn(
          'flex gap-2 items-center',
          "hover:bg-neutrals-200 text-neutrals-800 focus:bg-neutrals-200 font-semibold [&_svg:not([class*='text-'])]:text-muted-foreground rounded-md px-2 py-2 cursor-pointer text-sm transition-all outline-hidden [&_svg:not([class*='size-'])]:size-4",
          className,
        )}
        {...props}
      />
    </div>
  );
}

function NavigationMenuContentLink({
  className,
  active,
  ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.Link>) {
  return (
    <NavigationMenuPrimitive.Link
      data-slot="navigation-menu-link"
      className={cn(
        "hover:bg-neutrals-200 text-neutrals-900 focus:bg-neutrals-200 [&_svg:not([class*='text-'])]:text-muted-foreground flex flex-col gap-1 rounded-md p-2 text-sm transition-all outline-hidden [&_svg:not([class*='size-'])]:size-4",
        active ? 'font-bold' : '',
        className,
      )}
      {...props}
    />
  );
}

function NavigationMenuViewport({
  className,
  ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.Viewport>) {
  return (
    <div
      className={cn(
        'absolute top-full left-0 isolate z-50 flex justify-center',
      )}>
      <NavigationMenuPrimitive.Viewport
        data-slot="navigation-menu-viewport"
        className={cn(
          'origin-top-center bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-90 relative mt-1.5 h-[var(--radix-navigation-menu-viewport-height)] w-full overflow-hidden rounded-md border shadow md:w-[var(--radix-navigation-menu-viewport-width)]',
          className,
        )}
        {...props}
      />
    </div>
  );
}

function NavigationMenuIndicator({
  className,
  ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.Indicator>) {
  return (
    <NavigationMenuPrimitive.Indicator
      data-slot="navigation-menu-indicator"
      className={cn(
        'top-full z-1 flex h-1.5 items-end justify-center overflow-hidden data-[state=visible]:animate-in data-[state=hidden]:animate-out data-[state=hidden]:fade-out data-[state=visible]:fade-in',
        className,
      )}
      {...props}>
      <div className="relative top-[60%] h-2 w-2 rotate-45 rounded-tl-sm bg-border shadow-md" />
    </NavigationMenuPrimitive.Indicator>
  );
}

export {
  navigationMenuTriggerStyle,
  NavigationMenu,
  NavigationMenuList,
  NavigationMenuItem,
  NavigationMenuContent,
  NavigationMenuTrigger,
  NavigationMenuLink,
  NavigationMenuContentLink,
  NavigationMenuIndicator,
  NavigationMenuViewport,
};
